// Transform RDS data to frontend format with URL integration
export const transformCompanyData = (company, storeUrls = null) => {
    const storeName = company.company_name ?? 'Unknown';

    // Find matching store URLs
    const matchingStore = storeUrls?.find(store =>
        store.storeName === storeName ||
        store.storeName.toLowerCase().includes(storeName.toLowerCase()) ||
        storeName.toLowerCase().includes(store.storeName.toLowerCase())
    );

    // Build pages array from URL data
    let pages = [];

    if (matchingStore) {
        // Add Facebook pages
        matchingStore.facebook?.forEach(page => {
            if (page.url) {
                pages.push({
                    domain: 'Facebook',
                    type: page.type,
                    url: page.url,
                    lastChecked: null,
                    errors: [],
                    status: 'pending'
                });
            }
        });

        // Add PPC pages
        matchingStore.ppc?.forEach(page => {
            if (page.url) {
                pages.push({
                    domain: 'PPC',
                    type: page.type,
                    url: page.url,
                    lastChecked: null,
                    errors: [],
                    status: 'pending'
                });
            }
        });

        // Add More pages
        matchingStore.more?.forEach(page => {
            if (page.url) {
                pages.push({
                    domain: 'More',
                    type: page.type,
                    url: page.url,
                    lastChecked: null,
                    errors: [],
                    status: 'pending'
                });
            }
        });
    }

    // Fallback to original landing pages if no URL data found
    if (pages.length === 0 && company.landingPages) {
        pages = company.landingPages.map(page => ({
            domain: page.domain || '',
            type: page.type || '',
            url: page.url || '',
            lastChecked: null,
            errors: [],
            status: 'pending'
        }));
    }

    return {
        _id: (company.company_id ?? company.company_name ?? '').toString(),
        name: storeName,
        region: company.company_state || 'Unknown',
        license: 'Standard',
        address: `${company.company_address || ''}, ${company.company_city || ''}, ${company.company_state || ''}`.replace(/^,\s*|,\s*$/g, ''),
        website: company.company_website || '',
        contact: {
            name: company.company_contact_name || '',
            email: company.company_contact_email || ''
        },
        pages: pages
    };
};

// Load stores with validation results
export const loadStoresWithValidation = async (authenticatedFetch, transformCompanyData) => {
    try {
        // Fetch both stores and URL data with authentication
        const [storesResponse, urlsResponse] = await Promise.all([
            authenticatedFetch("/api/stores"),
            authenticatedFetch("/api/rds/store-urls")
        ]);

        if (!storesResponse.ok) {
            throw new Error(`HTTP error! status: ${storesResponse.status}`);
        }
        if (!urlsResponse.ok) {
            throw new Error(`HTTP error! status: ${urlsResponse.status}`);
        }

        const storesData = await storesResponse.json();
        const urlsData = await urlsResponse.json();

        if (!storesData.success) {
            throw new Error(storesData.error || 'Stores API request failed');
        }
        if (!urlsData.success) {
            throw new Error(urlsData.error || 'URLs API request failed');
        }

        // Transform RDS companies to frontend format with URL integration
        const stores = storesData.companies.map(company =>
            transformCompanyData(company, urlsData.storeUrls)
        );

        // Load validation results for all stores
        const storesWithValidation = await Promise.all(
            stores.map(async (store) => {
                try {
                    const validationResponse = await authenticatedFetch(`/api/stores/${encodeURIComponent(store.name)}/validation-results`);
                    if (validationResponse.ok) {
                        const validationData = await validationResponse.json();
                        if (validationData.success && validationData.validationResult) {
                            return { ...store, validationResults: validationData.validationResult };
                        }
                    }
                } catch (error) {
                    console.log('No validation results for', store.name);
                }
                return store;
            })
        );

        return storesWithValidation;
    } catch (error) {
        console.error('Failed to fetch stores from RDS:', error);
        throw error;
    }
};

// Create validation function for a store
export const createValidationFunction = (store, setValidationProgressByStore, handleValidationComplete) => {
    return () => {
        return new Promise(async (resolve, reject) => {
            try {
                const { fetchAuthSession } = await import('aws-amplify/auth');
                const session = await fetchAuthSession();
                const token = session.tokens?.idToken?.toString();

                if (!token) {
                    throw new Error('No authentication token available');
                }

                const baseUrl = store.isTestStore
                    ? `/api/stores/test-stores/${encodeURIComponent(store.testStoreName)}/validate-progress`
                    : `/api/stores/${encodeURIComponent(store.name)}/validate-progress`;

                const validationUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
                const eventSource = new EventSource(validationUrl);

                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        if (data.type === 'progress') {
                            setValidationProgressByStore(prev => ({
                                ...prev,
                                [store.name]: {
                                    currentPage: data.currentPage || 0,
                                    totalPages: data.totalPages || 0,
                                    currentPageName: data.currentPageName || data.message || 'Processing...',
                                    isActive: true
                                }
                            }));
                        } else if (data.type === 'complete') {
                            if (data.validationResults) {
                                handleValidationComplete(store.name, data.validationResults);
                            }
                            setValidationProgressByStore(prev => {
                                const updated = { ...prev };
                                delete updated[store.name];
                                return updated;
                            });
                            eventSource.close();
                            resolve();
                        } else if (data.type === 'error') {
                            setValidationProgressByStore(prev => {
                                const updated = { ...prev };
                                delete updated[store.name];
                                return updated;
                            });
                            eventSource.close();
                            reject(new Error(data.error || data.message || 'Validation failed'));
                        }
                    } catch (parseError) {
                        eventSource.close();
                        reject(parseError);
                    }
                };

                eventSource.onerror = (error) => {
                    setValidationProgressByStore(prev => {
                        const updated = { ...prev };
                        delete updated[store.name];
                        return updated;
                    });
                    eventSource.close();
                    reject(new Error('Connection error during validation'));
                };
            } catch (error) {
                reject(error);
            }
        });
    };
};
